/**
 * Wire schemas for the Operations **Dropped** surface — the terminal tab for
 * cases a Handling Firm took to **Placed** and then dropped (terminated
 * representation before the case settled).
 *
 * A drop is a Placement at Decision = "Dropped" (the portal stamps it at drop
 * time, requiring a Drop Reason + a Drop Letter). Dropped is a *terminal*
 * status — the case does not come back. The surface exists for the paperwork
 * that has to follow a drop: LF must hold the firm's **letter of termination**
 * (the drop letter), and LF must **notify the Originating Firm** that the case
 * is shut down. It sits last in the Operations lineup (after the would-be
 * Closed tab) because drops are comparatively rare.
 *
 * Not a salvage surface. An earlier sketch (CONTEXT.md's "sick bay", the
 * Settled book port-note) imagined Dropped as a rescue worklist —
 * newest-first, "salvage it back into the Pipeline". Chris resolved it the
 * other way (2026-05-22): Dropped is terminal, and this surface is read-only
 * paperwork tracking, not intervention.
 *
 * Read-only this pass. The card carries the five reads that have a data
 * source — client, originating + handling firm, drop date, drop reason — plus
 * the drop-letter gate (whether the termination letter is on file). The
 * **OF-notified** gate Chris also wants has no field and no automation today
 * (the portal notifies LF admin on drop, never the OF), so it is a deliberate
 * fast-follow write, not part of this read-only slice.
 *
 * The join: the population is authoritatively the set of placements at
 * Decision = "Dropped". The composer is placement-driven — it fetches the
 * leads behind those placements BY ID, not by status, because a dropped
 * placement's lead can sit at Lead Status "Dropped" (the norm), "Placed" (a
 * straggler whose status was never flipped), or even "Rejected" (a later
 * reroute failed / off-path data). Verified live 2026-05-22: the 26 dropped
 * placements' leads were 22 Dropped, 2 Placed, 2 Rejected. A status-keyed
 * fetch would make the non-Dropped ones invisible. See the port-note.
 *
 * Spec: engineering/command-center/docs/port-notes/dropped.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";
import {
  PANEL_DETAIL_LEAD_FIELDS,
  PLACEMENT_DETAIL_FIELDS,
} from "./placement-detail.js";

/**
 * One dropped case. The unit is the **placement** that was dropped
 * (Decision = Dropped); its lead supplies the client identity. The composer
 * does the joins so the UI renders without its own lookups.
 *
 * Drop Date and Drop Reason are nullable on purpose: the portal requires both
 * at drop time, but older and ops-entered (off-path) drops in the live data
 * lack one or both. The card shows what's there and reads the gaps honestly.
 */
export const DroppedCaseSchema = z.object({
  /** The dropped Placement's record ID — the case's identity here. */
  id: z.string(),
  /** Lead record ID — for joining to other surfaces / the future detail. */
  leadRecordId: z.string(),
  /** Stable human-readable Lead ID (Airtable primary text). */
  leadId: z.string(),
  /** Client name. */
  client: z.string(),

  /** Joined Originating Firm name (the OF to notify of the shutdown), or
   *  null when the link is missing/unknown. */
  originatingFirmName: z.string().nullable(),
  /** Joined Handling Firm name (the firm that dropped the case), or null. */
  handlingFirmName: z.string().nullable(),

  /** Raw Drop Date (ISO date) off the placement, or null when unrecorded.
   *  The within-group sort axis (newest drop first). */
  dropDate: z.string().nullable(),
  /** Why the firm terminated representation, or null when unrecorded. */
  dropReason: z.string().nullable(),
  /** TRUE when the firm's letter of termination (the drop letter) is on
   *  file. The paperwork gate — a drop with no letter is the work. */
  hasDropLetter: z.boolean(),

  // Panel-detail lead-context fields (Aesthetic Parity, Phase 2) — the joined
  // lead's transfer flag, received date, case file, and FSA document, defined
  // once in PANEL_DETAIL_LEAD_FIELDS. Additive (ADR-0133).
  ...PANEL_DETAIL_LEAD_FIELDS,

  // Class-B placement fields (slice 4 — panel detail). Historical filing
  // deadline + the monthly-update log up to the drop.
  ...PLACEMENT_DETAIL_FIELDS,
});
export type DroppedCase = z.infer<typeof DroppedCaseSchema>;

/**
 * Payload for `GET /api/dropped`. `cases` is the full drop book, sorted
 * paperwork-first: cases still missing the drop letter on top (the work),
 * then by drop date newest-first (unrecorded drop dates last).
 */
export const DroppedResponseSchema = z.object({
  cases: z.array(DroppedCaseSchema),
});
export type DroppedResponse = z.infer<typeof DroppedResponseSchema>;
